import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

type Column = "main" | "rail";

// HR-11, PG-57: the wide split of a widget surface (Home, Progress). MAIN takes
// two thirds, the RAIL one third capped at 380 so its compact cards do not
// balloon on an ultra-wide screen. Each column keeps the athlete's one order.
// All rail widgets hidden: main takes the whole width. Narrow: one list, the
// host's own flow.
export function WidgetColumns<Id extends string>({
  order,
  columnOf,
  wide,
  renderItem,
  gap,
  railTopInset,
}: {
  order: readonly Id[];
  columnOf: (id: Id) => Column;
  wide: boolean;
  /** Returns a keyed node, or null for a widget with nothing to show. */
  renderItem: (id: Id) => ReactNode;
  /** Between the two columns and between the cards in each. Home's cards
   *  carry their own margins and pass none. */
  gap?: number;
  /** Home lines the rail's first card up with the hero's top margin. */
  railTopInset?: number;
}) {
  if (!wide) return <>{order.map(renderItem)}</>;
  const main = order.filter((id) => columnOf(id) === "main");
  const rail = order.filter((id) => columnOf(id) === "rail");
  return (
    <View style={[widgetColumnStyles.cols, { gap }]}>
      <View style={[widgetColumnStyles.main, { gap }]}>{main.map(renderItem)}</View>
      {rail.length > 0 ? (
        <View style={[widgetColumnStyles.rail, { gap, paddingTop: railTopInset }]}>
          {rail.map(renderItem)}
        </View>
      ) : null}
    </View>
  );
}

/** The one split style: the widgets above and the config sheet's two lists. */
export const widgetColumnStyles = StyleSheet.create({
  // flex-start so the columns don't stretch to equal height.
  cols: { flexDirection: "row", alignItems: "flex-start" },
  main: { flex: 2, minWidth: 0 },
  rail: { flex: 1, minWidth: 0, maxWidth: 380 },
});
